import { If, onMount, reactive } from 'cdui-js';
import {
  fetchCrsLatest,
  normalizeCrsInt64Id,
  submitCrs,
  type UserCrsAttachment,
  type UserCrsRecord,
  type UserCrsSaveReq,
  type UserCrsTaxInfoReq,
} from '../../../api/crs';
import i18n from '../../../i18n';
import { goBackByBrowser, routeReplace } from '../../../lib/browser';
import { userState } from '../../../stores/user';
import { DiFiInput } from '../../../components/DiFi/DiFiInput';
import { SelectBox } from '../components/SelectBox';
import { CrsCountrySelect } from './CountrySelect';
import { crsCountryLabel, loadCrsCountryDict, resolveCrsCountryCode } from './countryDict';
import { AuthSensitiveImage } from '../components/AuthSensitiveImage';
import { PROOF_IMAGE_MAX_BYTES, UploadImage } from '../components/Upload';
import { showToast } from '../components/Toast';
import { IconUploadPlus } from '../deposit/h5-deposit/icons';
import { bootDepositApp } from '../deposit/h5-deposit/appBridge';
import {
  CRS_RESULT_KIND,
  CRS_TAX_STATUS,
  crsResultKindFromStatus,
  type CrsTaxStatus,
} from './constants';
import { CrsNav } from './Nav';
import {
  loadCityOptions,
  loadProvinceOptions,
  regionLabelOf,
  withRecordOption,
  type RegionOption,
} from './region';
import { crsSubmittedPath, readCrsReapplyQuery, syncCrsAppShell } from './shared';
import './page.css';

type CrsFormFieldProps = {
  label: string;
  children: unknown;
  error?: string;
};

const CrsFormField = (props: CrsFormFieldProps) => (
  <div class={`crs-field${props.error ? ' crs-field-error' : ''}`}>
    <div class="crs-field-label font-s-S color">{props.label}</div>
    <div class="crs-field-control">{props.children}</div>
    {props.error ? <div class="crs-field-err font-s-XS color-error">{props.error}</div> : null}
  </div>
);

const filePathOf = (data: unknown) => {
  const row = data as Record<string, unknown>;
  if (typeof row?.filePath === 'string') return row.filePath.trim();
  const nested = row?.data as Record<string, unknown> | undefined;
  if (typeof nested === 'string') return nested.trim();
  if (typeof nested?.filePath === 'string') return nested.filePath.trim();
  return '';
};

const proofPathFromAttachment = (attachment?: UserCrsAttachment) =>
  String(attachment?.filePath || attachment?.fileKey || attachment?.fileUrl || '').trim();

const countryLabel = (code: string) => (code ? crsCountryLabel(code) || code : '');

const taxStatusOptions = () => [
  { value: CRS_TAX_STATUS.PROVIDED, label: i18n.Crs.TaxStatusProvided },
  { value: CRS_TAX_STATUS.NOT_PROVIDED, label: i18n.Crs.TaxStatusNotProvided },
];

const emptyErrors = () => ({
  country: '',
  province: '',
  city: '',
  postalCode: '',
  taxCountry: '',
  taxStatus: '',
  taxNumber: '',
  reason: '',
  proof: '',
});

/** CRS 等级登记表单 */
export const CrsFormPage = () => {
  const state = reactive({
    country: '',
    province: '',
    city: '',
    postalCode: '',
    taxCountry: '',
    taxStatus: '' as CrsTaxStatus | '',
    taxNumber: '',
    reason: '',
    proofPath: '',
    proofPreview: '',
    baseId: '',
    uploading: false,
    submitting: false,
    provinceOptions: [] as RegionOption[],
    cityOptions: [] as RegionOption[],
    regionLoading: false,
    hydrating: true,
    errors: emptyErrors(),
  });

  const resetProvinceCity = () => {
    state.province = '';
    state.city = '';
    state.provinceOptions = [];
    state.cityOptions = [];
    state.errors.province = '';
    state.errors.city = '';
  };

  /** 快速切换国家/省时丢弃旧请求的结果 */
  let regionSeq = 0;

  const onCountryChange = (value: string | number) => {
    const next = String(value || '');
    if (next === state.country) return;
    state.country = next;
    state.errors.country = '';
    resetProvinceCity();
    if (!next) return;
    const seq = ++regionSeq;
    state.regionLoading = true;
    void loadProvinceOptions(next)
      .then((rows) => {
        if (seq === regionSeq) state.provinceOptions = rows;
      })
      .finally(() => {
        if (seq === regionSeq) state.regionLoading = false;
      });
  };

  const onProvinceChange = (value: string | number) => {
    const next = String(value || '');
    if (next === state.province) return;
    state.province = next;
    state.city = '';
    state.cityOptions = [];
    state.errors.province = '';
    state.errors.city = '';
    if (!next) return;
    const seq = ++regionSeq;
    state.regionLoading = true;
    void loadCityOptions(next)
      .then((rows) => {
        if (seq === regionSeq) state.cityOptions = rows;
      })
      .finally(() => {
        if (seq === regionSeq) state.regionLoading = false;
      });
  };

  const onTaxStatusChange = (value: string | number) => {
    const next = String(value) as CrsTaxStatus;
    state.taxStatus = next;
    state.errors.taxStatus = '';
    if (next === CRS_TAX_STATUS.PROVIDED) {
      state.reason = '';
      state.proofPath = '';
      state.proofPreview = '';
      state.errors.reason = '';
      state.errors.proof = '';
    } else if (next === CRS_TAX_STATUS.NOT_PROVIDED) {
      state.taxNumber = '';
      state.errors.taxNumber = '';
    }
  };

  const onUploadChange = (payload: unknown) => {
    const row = payload as Record<string, unknown>;
    if (row?.status === 'loading') {
      state.uploading = true;
      return;
    }
    state.uploading = false;
    if (row?.status === 'error') return;
    const path = filePathOf(payload);
    if (!path) return;
    state.proofPath = path;
    const preview = typeof row?.localPreviewUrl === 'string' ? row.localPreviewUrl : path;
    state.proofPreview = preview;
    state.errors.proof = '';
  };

  const buildSubmitBody = (): UserCrsSaveReq => {
    const body: UserCrsSaveReq = {
      entityType: 'INDIVIDUAL',
      clientChannel: 'H5',
    };
    const put = <K extends keyof UserCrsSaveReq>(key: K, value: string) => {
      const v = value.trim();
      if (v) body[key] = v as UserCrsSaveReq[K];
    };
    put('countryCode', state.country);
    put('country', countryLabel(state.country));
    if (state.province) {
      put('provinceCode', state.province);
      put('province', regionLabelOf(state.provinceOptions, state.province));
    }
    if (state.city) {
      put('cityCode', state.city);
      put('city', regionLabelOf(state.cityOptions, state.city));
    }
    put('postcode', state.postalCode);

    if (state.taxCountry || state.taxStatus) {
      const tax: UserCrsTaxInfoReq = { sortNo: 1 };
      if (state.taxCountry) tax.taxCountry = state.taxCountry;
      if (state.taxStatus) tax.taxStatus = state.taxStatus;
      if (state.taxStatus === CRS_TAX_STATUS.PROVIDED && state.taxNumber.trim()) {
        tax.tin = state.taxNumber.trim();
      }
      if (state.taxStatus === CRS_TAX_STATUS.NOT_PROVIDED && state.reason.trim()) {
        tax.reasonDesc = state.reason.trim();
      }
      body.taxInfos = [tax];
    }
    const baseId = normalizeCrsInt64Id(state.baseId);
    if (baseId) body.baseId = baseId;
    const proofPath = state.proofPath.trim();
    if (state.taxStatus === CRS_TAX_STATUS.NOT_PROVIDED && proofPath) {
      body.attachmentFilePaths = [proofPath];
    }
    return body;
  };

  /** needNewYear：往年记录只回填内容，不带 baseId（按当年新建） */
  const applyLatest = async (record: UserCrsRecord, needNewYear: boolean) => {
    const countryCode = resolveCrsCountryCode(String(record.countryCode || record.country || ''));
    state.country = countryCode;
    state.postalCode = String(record.postcode || '');
    if (countryCode) {
      const provinceCode = String(record.provinceCode || '');
      state.provinceOptions = withRecordOption(
        await loadProvinceOptions(countryCode),
        provinceCode,
        String(record.province || ''),
      );
      state.province = provinceCode;
      if (provinceCode) {
        const cityCode = String(record.cityCode || '');
        state.cityOptions = withRecordOption(
          await loadCityOptions(provinceCode),
          cityCode,
          String(record.city || ''),
        );
        state.city = cityCode;
      }
    }
    const tax = record.taxInfos?.[0];
    if (tax) {
      state.taxCountry = resolveCrsCountryCode(String(tax.taxCountry || ''));
      const status = String(tax.taxStatus || '');
      state.taxStatus =
        status === CRS_TAX_STATUS.PROVIDED || status === CRS_TAX_STATUS.NOT_PROVIDED
          ? status
          : '';
      state.taxNumber = String(tax.tin || '');
      state.reason = String(tax.reasonDesc || tax.reasonCode || '');
    }
    const attachment = record.attachments?.[0];
    const path = proofPathFromAttachment(attachment);
    if (state.taxStatus === CRS_TAX_STATUS.NOT_PROVIDED && path) {
      state.proofPath = path;
      state.proofPreview = path;
    }
    const recordId = normalizeCrsInt64Id(record.id);
    state.baseId = !needNewYear && recordId ? recordId : '';
  };

  const validate = () => {
    const errors = emptyErrors();
    if (!state.country) errors.country = i18n.Crs.ErrCountry;
    if (state.provinceOptions.length && !state.province) errors.province = i18n.Crs.ErrProvince;
    if (state.cityOptions.length && !state.city) errors.city = i18n.Crs.ErrCity;
    if (state.regionLoading) errors.province = errors.province || i18n.Crs.ErrProvince;
    if (!state.postalCode.trim()) errors.postalCode = i18n.Crs.ErrPostalCode;
    if (!state.taxCountry) errors.taxCountry = i18n.Crs.ErrTaxCountry;
    if (!state.taxStatus) errors.taxStatus = i18n.Crs.ErrTaxStatus;
    if (state.taxStatus === CRS_TAX_STATUS.PROVIDED && !state.taxNumber.trim()) {
      errors.taxNumber = i18n.Crs.ErrTaxNumber;
    }
    if (state.taxStatus === CRS_TAX_STATUS.NOT_PROVIDED) {
      if (!state.reason.trim()) errors.reason = i18n.Crs.ErrReason;
      if (!state.proofPath.trim()) errors.proof = i18n.Crs.ErrProof;
    }
    state.errors = errors;
    const first = Object.values(errors).find(Boolean);
    if (first) showToast(first);
    return !first;
  };

  const onSubmit = async () => {
    if (state.submitting || state.uploading || state.hydrating) return;
    if (!validate()) return;
    state.submitting = true;
    try {
      await submitCrs(buildSubmitBody());
      // 提交后进入审批流：统一进审核中结果页（SUBMITTED / APPROVING）
      routeReplace(crsSubmittedPath(CRS_RESULT_KIND.PENDING), [0, 0]);
    } catch (error) {
      const msg =
        typeof error === 'string'
          ? error
          : (error as { message?: string })?.message || i18n.Crs.SubmitFail;
      showToast(msg);
    } finally {
      state.submitting = false;
    }
  };

  const onBack = () => {
    goBackByBrowser();
  };

  onMount(async () => {
    syncCrsAppShell();
    await bootDepositApp();
    // userInfo 由 App bootstrap 写入 userState；上传 busId 读全局，滞后可回退 0
    try {
      const latest = await fetchCrsLatest();
      const record = latest?.record;

      // 当年已在审批中 / 已通过 / 已拒绝：直接进结果页，禁止再改表单
      // reapply=1（拒绝页「重新申请」）仅跳过 REFUSED 跳转，回填后留在表单
      // needNewYear（往年记录）仍进表单，按当年新建；routeReplace 避免返回又回到空白表单
      if (record && latest.needNewYear !== true) {
        const resultKind = crsResultKindFromStatus(String(record.status || ''));
        const skipRefusedRedirect =
          resultKind === CRS_RESULT_KIND.REFUSED && readCrsReapplyQuery();
        if (resultKind && !skipRefusedRedirect) {
          routeReplace(crsSubmittedPath(resultKind), [0, 0]);
          return;
        }
      }

      try {
        await loadCrsCountryDict();
      } catch {
        // 国家字典失败时仍尝试回填码
      }
      if (record) await applyLatest(record, latest?.needNewYear === true);
    } catch {
      // 无历史记录时空白新建；仍尝试加载国家字典
      try {
        await loadCrsCountryDict();
      } catch {
        // ignore
      }
    } finally {
      state.hydrating = false;
    }
  });

  return (
    <div class="crs-h5">
      <CrsNav title={i18n.Crs.Title} onBack={onBack} />
      <div class="crs-scroll padding-m-x">
        <section class="crs-section">
          <h2 class="crs-section-title font-s color-D">{i18n.Crs.SectionInfo}</h2>
          <CrsFormField label={i18n.Crs.LabelCountry} error={state.errors.country}>
            <CrsCountrySelect
              title={i18n.Crs.PhCountry}
              placeholder={i18n.Crs.PhCountry}
              value={state.country}
              change={onCountryChange}
            />
          </CrsFormField>
          <CrsFormField label={i18n.Crs.LabelProvince} error={state.errors.province}>
            <SelectBox
              title={i18n.Crs.PhProvince}
              placeholder={i18n.Crs.PhProvince}
              value={state.province}
              options={state.provinceOptions}
              disabled={!state.country || state.regionLoading || !state.provinceOptions.length}
              change={onProvinceChange}
            />
          </CrsFormField>
          <CrsFormField label={i18n.Crs.LabelCity} error={state.errors.city}>
            <SelectBox
              title={i18n.Crs.PhCity}
              placeholder={i18n.Crs.PhCity}
              value={state.city}
              options={state.cityOptions}
              disabled={!state.province || state.regionLoading || !state.cityOptions.length}
              change={(value) => {
                state.city = String(value);
                state.errors.city = '';
              }}
            />
          </CrsFormField>
          <CrsFormField label={i18n.Crs.LabelPostalCode} error={state.errors.postalCode}>
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={32}
              placeholder={i18n.Crs.PhPostalCode}
              value={state.postalCode}
              onUpdateValue={(v) => {
                state.postalCode = v;
                state.errors.postalCode = '';
              }}
            />
          </CrsFormField>
        </section>

        <section class="crs-section">
          <h2 class="crs-section-title font-s color-D">{i18n.Crs.SectionTax}</h2>
          <CrsFormField label={i18n.Crs.LabelTaxCountry} error={state.errors.taxCountry}>
            <CrsCountrySelect
              title={i18n.Crs.PhTaxCountry}
              placeholder={i18n.Crs.PhTaxCountry}
              value={state.taxCountry}
              change={(value) => {
                state.taxCountry = value;
                state.errors.taxCountry = '';
              }}
            />
          </CrsFormField>
          <CrsFormField label={i18n.Crs.LabelTaxStatus} error={state.errors.taxStatus}>
            <SelectBox
              title={i18n.Crs.LabelTaxStatus}
              placeholder={i18n.Crs.PhTaxStatus}
              value={state.taxStatus}
              options={taxStatusOptions()}
              change={onTaxStatusChange}
            />
          </CrsFormField>

          <If when={state.taxStatus === CRS_TAX_STATUS.PROVIDED}>
            <CrsFormField label={i18n.Crs.LabelTaxNumber} error={state.errors.taxNumber}>
              <DiFiInput
                shellClass="f-input-2"
                inputKind="text"
                maxLength={64}
                placeholder={i18n.Crs.PhTaxNumber}
                value={state.taxNumber}
                onUpdateValue={(v) => {
                  state.taxNumber = v;
                  state.errors.taxNumber = '';
                }}
              />
            </CrsFormField>
          </If>

          <If when={state.taxStatus === CRS_TAX_STATUS.NOT_PROVIDED}>
            <CrsFormField label={i18n.Crs.LabelReason} error={state.errors.reason}>
              <DiFiInput
                shellClass="f-input-2"
                inputKind="text"
                maxLength={200}
                placeholder={i18n.Crs.PhReason}
                value={state.reason}
                onUpdateValue={(v) => {
                  state.reason = v;
                  state.errors.reason = '';
                }}
              />
            </CrsFormField>
          </If>

          <ul class="crs-notes font-s-XS color-L">
            <li>{i18n.Crs.NoteTaxNumber}</li>
            <li>{i18n.Crs.NoteContactRm}</li>
          </ul>
        </section>

        <If when={state.taxStatus === CRS_TAX_STATUS.NOT_PROVIDED}>
          <section class="crs-section">
            <h2 class="crs-section-title font-s color-D">{i18n.Crs.SectionUpload}</h2>
            <div class="crs-upload-row">
              <If when={state.proofPreview}>
                <div class="crs-upload-slot crs-upload-preview">
                  <AuthSensitiveImage
                    path={state.proofPreview}
                    style={{ width: '100%', height: '100%', 'object-fit': 'cover' }}
                  />
                  <button
                    type="button"
                    class="crs-upload-remove"
                    onclick={() => {
                      state.proofPath = '';
                      state.proofPreview = '';
                    }}
                  >
                    ×
                  </button>
                </div>
              </If>
              <If when={!state.proofPreview}>
                <div class="crs-upload-slot crs-upload-add">
                  <IconUploadPlus class="crs-upload-add-icon" />
                  <UploadImage
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      'z-index': 2,
                    }}
                    useFor="crs_supporting_material"
                    busId={String(userState.id || '0')}
                    restrictImageType={true}
                    maxSize={PROOF_IMAGE_MAX_BYTES}
                    compressBeforeUpload={true}
                    change={onUploadChange}
                  />
                </div>
              </If>
            </div>
            {state.errors.proof ? <div class="crs-field-err font-s-XS color-error">{state.errors.proof}</div> : null}
            <p class="crs-upload-hint font-s-XS color-L">{i18n.Crs.UploadHint}</p>
          </section>
        </If>
      </div>
      <div class="crs-foot">
        <button
          type="button"
          class="crs-primary-btn"
          disabled={state.submitting || state.uploading}
          onclick={onSubmit}
        >
          {state.submitting ? i18n.Crs.Submitting : i18n.Crs.Submit}
        </button>
      </div>
    </div>
  );
};
